import React from "react";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import {
  isPatriotSendVideoOn,
  usePatriotIgnoreZonesOnAlertPage,
} from "./PatriotDrawIgnoreZonesGate";

// One choice for what a Patriot alert delivers, replacing the separate
// "Patriot send video" / "Draw Ignore Zones" checkboxes (ENG-2405). It still
// reads and writes the same two stored fields, so no backend change:
//   link          -> patriot_send_video=false, draw_ignore_zones=false
//   linkWithZones -> patriot_send_video=false, draw_ignore_zones=true
//   video         -> patriot_send_video=true,  draw_ignore_zones=<checkbox>
export type PatriotDelivery = "link" | "linkWithZones" | "video";

export interface PatriotDeliveryValues {
  patriot_send_video: boolean;
  draw_ignore_zones: boolean;
}

export const PATRIOT_DELIVERY_LABEL = "What Patriot receives";

export const PATRIOT_DELIVERY_OPTION_LABELS = {
  link: "Alert link",
  linkWithZones: "Alert link with ignore zones",
  linkWithZonesComingSoon: "Alert link with ignore zones (coming soon)",
  video: "Video clip (MP4)",
} as const;

export const PATRIOT_VIDEO_DRAW_IGNORE_ZONES_LABEL =
  "Draw ignore zones on the video";

/** Derive the choice from the stored pair (values may be "True"/"true"). */
export const patriotDeliveryFromValues = (
  sendVideo: unknown,
  drawIgnoreZones: unknown
): PatriotDelivery => {
  if (isPatriotSendVideoOn(sendVideo)) {
    return "video";
  }
  return isPatriotSendVideoOn(drawIgnoreZones) ? "linkWithZones" : "link";
};

/**
 * The stored pair for a choice. For "video" the draw_ignore_zones value is
 * carried over from the current one (the MP4 checkbox owns it).
 */
export const patriotDeliveryToValues = (
  delivery: PatriotDelivery,
  currentDrawIgnoreZones: unknown
): PatriotDeliveryValues => {
  if (delivery === "video") {
    return {
      patriot_send_video: true,
      draw_ignore_zones: isPatriotSendVideoOn(currentDrawIgnoreZones),
    };
  }
  return {
    patriot_send_video: false,
    draw_ignore_zones: delivery === "linkWithZones",
  };
};

/** Read-only text for a stored pair, e.g. in the Alert Settings table. */
export const patriotDeliveryDisplayText = (
  sendVideo: unknown,
  drawIgnoreZones: unknown,
  ignoreZonesOnAlertPage: boolean
): string => {
  const delivery = patriotDeliveryFromValues(sendVideo, drawIgnoreZones);
  if (delivery === "video") {
    return isPatriotSendVideoOn(drawIgnoreZones)
      ? `${PATRIOT_DELIVERY_OPTION_LABELS.video}, ignore zones drawn`
      : PATRIOT_DELIVERY_OPTION_LABELS.video;
  }
  if (delivery === "linkWithZones") {
    return ignoreZonesOnAlertPage
      ? PATRIOT_DELIVERY_OPTION_LABELS.linkWithZones
      : PATRIOT_DELIVERY_OPTION_LABELS.linkWithZonesComingSoon;
  }
  return PATRIOT_DELIVERY_OPTION_LABELS.link;
};

/**
 * Radio choice for a Patriot alert's delivery. "Alert link with ignore zones"
 * only appears with the alert-page flag on; with it off, a stored
 * link-with-zones pair still shows (selected, disabled, "coming soon") rather
 * than being silently rewritten.
 */
export const PatriotDeliveryChoice = ({
  sendVideo,
  drawIgnoreZones,
  onChange,
  name = "patriot-delivery",
  showLabel = true,
}: {
  sendVideo: unknown;
  drawIgnoreZones: unknown;
  onChange: (values: PatriotDeliveryValues) => void;
  name?: string;
  showLabel?: boolean;
}) => {
  const ignoreZonesOnAlertPage = usePatriotIgnoreZonesOnAlertPage();
  const delivery = patriotDeliveryFromValues(sendVideo, drawIgnoreZones);
  const showLinkWithZones =
    ignoreZonesOnAlertPage || delivery === "linkWithZones";
  const labelId = `${name}-label`;

  return (
    // Fixed width so picking an option never resizes the table column the
    // choice sits in (which shifted the whole row sideways).
    <FormControl
      data-testid="patriot-delivery-choice"
      sx={{ minWidth: "17rem" }}
    >
      {showLabel && (
        <FormLabel id={labelId} sx={{ fontFamily: "Mulish", color: "#001943" }}>
          {PATRIOT_DELIVERY_LABEL}
        </FormLabel>
      )}
      <RadioGroup
        aria-label={showLabel ? undefined : PATRIOT_DELIVERY_LABEL}
        aria-labelledby={showLabel ? labelId : undefined}
        name={name}
        value={delivery}
        onChange={(event) => {
          const next = event.target.value as PatriotDelivery;
          onChange(patriotDeliveryToValues(next, drawIgnoreZones));
        }}
      >
        <FormControlLabel
          value="link"
          control={<Radio color="primary" size="small" />}
          label={PATRIOT_DELIVERY_OPTION_LABELS.link}
        />
        {showLinkWithZones && (
          <FormControlLabel
            value="linkWithZones"
            disabled={!ignoreZonesOnAlertPage}
            control={<Radio color="primary" size="small" />}
            label={
              ignoreZonesOnAlertPage
                ? PATRIOT_DELIVERY_OPTION_LABELS.linkWithZones
                : PATRIOT_DELIVERY_OPTION_LABELS.linkWithZonesComingSoon
            }
          />
        )}
        <FormControlLabel
          value="video"
          control={<Radio color="primary" size="small" />}
          label={PATRIOT_DELIVERY_OPTION_LABELS.video}
        />
      </RadioGroup>
      {/* Always rendered so choosing MP4 doesn't change the cell's height;
          hidden and disabled unless the MP4 option is selected. */}
      <Box
        aria-hidden={delivery !== "video"}
        sx={{ visibility: delivery === "video" ? "visible" : "hidden" }}
      >
        <FormControlLabel
          sx={{ marginLeft: "1.5rem" }}
          disabled={delivery !== "video"}
          label={PATRIOT_VIDEO_DRAW_IGNORE_ZONES_LABEL}
          control={
            <Checkbox
              color="primary"
              size="small"
              checked={isPatriotSendVideoOn(drawIgnoreZones)}
              onChange={(event, checked) => {
                onChange({
                  patriot_send_video: true,
                  draw_ignore_zones: checked,
                });
              }}
            />
          }
        />
      </Box>
    </FormControl>
  );
};
